import React, { useMemo } from 'react';
import styled from 'styled-components';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LabelList, PieChart, Pie, Cell, Legend, AreaChart, Area
} from 'recharts';
import { colors } from '../../theme/colors';

/**
 * Super Admin → Insights tab.
 *
 * Graphs of the whole company base. EVERYTHING is derived in-memory from the
 * `companies` array the Companies tab already loaded — so this tab makes ZERO
 * extra Firestore reads. All fields used (heardFrom, subscription, status,
 * totalDocs, city, country, whatsappNumber, subscription.salesPersonName) are
 * already on each company object. Shadow/tax companies (replicateFrom) are excluded so they don't
 * double-count a real company.
 */

// ── palette ──────────────────────────────────────────────────────────────────
const MAROON = colors.primary?.main || '#800020';
const STATUS = {
  active:    colors.success?.main || '#16A34A',   // paid & running
  trial:     '#D4A017',                            // amber — evaluating
  expired:   colors.neutral?.[400] || '#9CA3AF',   // grey — lapsed
  suspended: colors.error?.main || '#DC2626',      // red — blocked
};

const money = (n) => (n || 0).toLocaleString();

// Country from the owner's WhatsApp code, for companies that never filled in a
// country (signup does not ask for it). Longest codes first so +971 beats +97.
const DIAL_COUNTRIES = [
  ['+971', 'UAE'], ['+966', 'Saudi Arabia'], ['+974', 'Qatar'], ['+973', 'Bahrain'],
  ['+968', 'Oman'], ['+965', 'Kuwait'], ['+234', 'Nigeria'], ['+254', 'Kenya'],
  ['+92', 'Pakistan'], ['+44', 'UK'], ['+91', 'India'], ['+86', 'China'], ['+90', 'Turkey'],
  ['+49', 'Germany'], ['+33', 'France'], ['+61', 'Australia'], ['+27', 'South Africa'],
  ['+60', 'Malaysia'], ['+63', 'Philippines'], ['+1', 'USA/Canada'],
];
const countryFromWhatsapp = (wa) => {
  let v = String(wa || '').replace(/[^\d+]/g, '');
  if (/^00[1-9]/.test(v)) v = '+' + v.slice(2);          // 0092… → +92…
  else if (/^[1-9]\d{9,}$/.test(v)) v = '+' + v;          // 92300… (no plus)
  if (!v.startsWith('+')) return '';
  const hit = DIAL_COUNTRIES.find(([code]) => v.startsWith(code));
  return hit ? hit[1] : '';
};
const titleCase = (s) => s.replace(/\b\w/g, ch => ch.toUpperCase());
// Count by a text value, biggest first; the rest → "Others". "karachi",
// "Karachi " and "KARACHI" are one bar, shown in its most-used spelling.
const countBy = (list, getValue, top = 15) => {
  const m = {};
  list.forEach(c => {
    const raw = String(getValue(c) || '').trim().replace(/\s+/g, ' ');
    const key = raw.toLowerCase();
    if (!m[key]) m[key] = { value: 0, spellings: {} };
    m[key].value++;
    if (raw) m[key].spellings[raw] = (m[key].spellings[raw] || 0) + 1;
  });
  const all = Object.entries(m).map(([key, x]) => {
    if (!key) return { name: 'Not specified', value: x.value };
    const best = Object.entries(x.spellings).sort((a, b) => b[1] - a[1])[0][0];
    return { name: best === best.toLowerCase() ? titleCase(best) : best, value: x.value };
  }).sort((a, b) => b.value - a.value);
  if (all.length <= top) return all;
  const rest = all.slice(top).reduce((s, x) => s + x.value, 0);
  return [...all.slice(0, top), { name: `Others (${all.length - top})`, value: rest }];
};

// A tidy hover card reused by every chart.
const TipBox = styled.div`
  background: #fff; border: 1px solid ${colors.neutral[200]};
  border-radius: 8px; box-shadow: 0 6px 20px rgba(0,0,0,0.12);
  padding: 8px 12px; font-size: 12px;
`;
const ChartTip = ({ active, payload, unit }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0];
  return (
    <TipBox>
      <div style={{ fontWeight: 600, color: colors.text.primary }}>{p.payload.name}</div>
      <div style={{ color: colors.text.secondary }}>{money(p.value)} {unit || ''}</div>
    </TipBox>
  );
};

// ── horizontal bar (category → count), single hue, direct value labels ───────
const HBar = ({ data, unit, color = MAROON, height }) => (
  <ResponsiveContainer width="100%" height={height || Math.max(160, data.length * 30 + 24)}>
    <BarChart data={data} layout="vertical" margin={{ top: 4, right: 34, left: 6, bottom: 4 }}>
      <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} horizontal={false} />
      <XAxis type="number" hide />
      <YAxis type="category" dataKey="name" width={150} tickLine={false} axisLine={false}
        stroke={colors.text.secondary} fontSize={11} />
      <Tooltip content={<ChartTip unit={unit} />} cursor={{ fill: `${color}0F` }} />
      <Bar dataKey="value" fill={color} radius={[0, 4, 4, 0]} maxBarSize={22}>
        <LabelList dataKey="value" position="right" fontSize={11} fill={colors.text.secondary}
          formatter={money} />
      </Bar>
    </BarChart>
  </ResponsiveContainer>
);

const InsightsTab = ({ companies = [] }) => {
  const real = useMemo(() => companies.filter(c => !c.replicateFrom), [companies]);

  // 0. Monthly signups — companies grouped by createdAt month, gap-filled into a
  //    continuous timeline (months with no signups show 0), last 18 months.
  const signupData = useMemo(() => {
    const counts = {};
    real.forEach(c => {
      const d = c.createdAt ? new Date(c.createdAt) : null;
      if (!d || isNaN(d.getTime())) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      counts[key] = (counts[key] || 0) + 1;
    });
    const keys = Object.keys(counts).sort();
    if (!keys.length) return [];
    let [y, m] = keys[0].split('-').map(Number);
    const now = new Date();
    const endY = now.getFullYear(), endM = now.getMonth() + 1;
    const out = [];
    while (y < endY || (y === endY && m <= endM)) {
      const key = `${y}-${String(m).padStart(2, '0')}`;
      out.push({ name: new Date(y, m - 1, 1).toLocaleString('en', { month: 'short', year: '2-digit' }), value: counts[key] || 0 });
      m++; if (m > 12) { m = 1; y++; }
    }
    return out.slice(-18);
  }, [real]);

  // 1. Heard About Us
  const heardData = useMemo(() => {
    const m = {};
    real.forEach(c => { const k = (c.heardFrom || '').trim() || 'Not specified'; m[k] = (m[k] || 0) + 1; });
    return Object.entries(m).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [real]);

  // 2. Docs count — the most active companies (top 12) + grand total
  const docsData = useMemo(() =>
    real.filter(c => (c.totalDocs || 0) > 0)
      .map(c => ({ name: c.companyName || '(unnamed)', value: c.totalDocs || 0 }))
      .sort((a, b) => b.value - a.value).slice(0, 12),
  [real]);
  const totalDocs = useMemo(() => real.reduce((s, c) => s + (c.totalDocs || 0), 0), [real]);

  // 3. Trial vs Active vs Suspended (subscription/status mix)
  const statusData = useMemo(() => {
    let trial = 0, active = 0, expired = 0, suspended = 0;
    real.forEach(c => {
      if (c.status === 'suspended') { suspended++; return; }
      const plan = c.subscription?.plan || 'trial';
      if (plan === 'monthly' || plan === 'yearly') active++;
      else if (plan === 'expired') expired++;
      else trial++;
    });
    return [
      { key: 'active', name: 'Active (paid)', value: active, color: STATUS.active },
      { key: 'trial', name: 'Trial', value: trial, color: STATUS.trial },
      { key: 'expired', name: 'Expired', value: expired, color: STATUS.expired },
      { key: 'suspended', name: 'Suspended', value: suspended, color: STATUS.suspended },
    ].filter(s => s.value > 0);
  }, [real]);

  // 4. Country — as entered on the company, else from the owner's WhatsApp code.
  const countryData = useMemo(() => countBy(real, c => (c.country || '').trim() || countryFromWhatsapp(c.whatsappNumber)), [real]);

  // 4b. City — as entered on the company (top 15, the rest grouped).
  const cityData = useMemo(() => countBy(real, c => c.city), [real]);

  // 5. Sales person — companies attributed to each
  const salesData = useMemo(() => {
    const m = {};
    real.forEach(c => { const k = (c.subscription?.salesPersonName || '').trim() || 'Unassigned'; m[k] = (m[k] || 0) + 1; });
    return Object.entries(m).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [real]);

  const tiles = [
    { label: 'Companies', value: real.length, color: MAROON },
    { label: 'Total Documents', value: totalDocs, color: colors.info?.main || '#2563EB' },
    { label: 'Active (paid)', value: statusData.find(s => s.key === 'active')?.value || 0, color: STATUS.active },
    { label: 'Trial', value: statusData.find(s => s.key === 'trial')?.value || 0, color: STATUS.trial },
    { label: 'Suspended', value: statusData.find(s => s.key === 'suspended')?.value || 0, color: STATUS.suspended },
  ];

  return (
    <Wrap>
      <TileRow>
        {tiles.map(t => (
          <Tile key={t.label}>
            <TileVal style={{ color: t.color }}>{money(t.value)}</TileVal>
            <TileLbl>{t.label}</TileLbl>
          </Tile>
        ))}
      </TileRow>

      <Note>Computed live from the loaded company list — no extra database reads.</Note>

      <Grid>
        <Card $wide>
          <CardTitle>Monthly signups</CardTitle>
          {signupData.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={signupData} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                <defs>
                  <linearGradient id="amalSignupGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={MAROON} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={MAROON} stopOpacity={0.03} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} vertical={false} />
                <XAxis dataKey="name" tickLine={false} axisLine={false} stroke={colors.text.secondary} fontSize={11} minTickGap={12} />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} stroke={colors.text.secondary} fontSize={11} width={34} />
                <Tooltip content={<ChartTip unit="signups" />} />
                <Area type="monotone" dataKey="value" stroke={MAROON} strokeWidth={2} fill="url(#amalSignupGrad)" dot={{ r: 2 }} activeDot={{ r: 4 }} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <Empty>No signups yet</Empty>}
        </Card>

        <Card>
          <CardTitle>How they heard about us</CardTitle>
          {heardData.length ? <HBar data={heardData} unit="companies" /> : <Empty>No data</Empty>}
        </Card>

        <Card>
          <CardTitle>Trial vs Active vs Suspended</CardTitle>
          {statusData.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={62} outerRadius={92}
                  paddingAngle={2} stroke="#fff" strokeWidth={2}>
                  {statusData.map(s => <Cell key={s.key} fill={s.color} />)}
                  <LabelList dataKey="value" position="outside" fontSize={12} fill={colors.text.secondary} />
                </Pie>
                <Tooltip content={<ChartTip unit="companies" />} />
                <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <Empty>No data</Empty>}
        </Card>

        <Card>
          <CardTitle>Companies by country</CardTitle>
          {countryData.length ? <HBar data={countryData} unit="companies" color="#0F766E" /> : <Empty>No data</Empty>}
        </Card>

        <Card>
          <CardTitle>Companies by city</CardTitle>
          {cityData.length ? <HBar data={cityData} unit="companies" color="#7C3AED" /> : <Empty>No data</Empty>}
        </Card>

        <Card $wide>
          <CardTitle>Companies per sales person</CardTitle>
          {salesData.length ? <HBar data={salesData} unit="companies" color={colors.info?.main || '#2563EB'} /> : <Empty>No data</Empty>}
        </Card>

        <Card $wide>
          <CardTitle>Most active companies (by document count)</CardTitle>
          {docsData.length ? <HBar data={docsData} unit="docs" color={colors.success?.main || '#16A34A'} height={380} /> : <Empty>No documents yet</Empty>}
        </Card>
      </Grid>
    </Wrap>
  );
};

// ── styles ───────────────────────────────────────────────────────────────────
const Wrap = styled.div``;
const TileRow = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 14px; margin-bottom: 14px;
`;
const Tile = styled.div`
  background: #fff; border: 1px solid ${colors.neutral[200]}; border-radius: 12px;
  padding: 16px 18px; box-shadow: ${colors.shadows?.sm || '0 1px 3px rgba(0,0,0,0.06)'};
`;
const TileVal = styled.div`font-size: 26px; font-weight: 700; line-height: 1.1;`;
const TileLbl = styled.div`font-size: 12px; color: ${colors.text.secondary}; margin-top: 4px;`;
const Note = styled.div`font-size: 12px; color: ${colors.text.tertiary || colors.text.secondary}; margin-bottom: 16px;`;
const Grid = styled.div`
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;
const Card = styled.div`
  background: #fff; border: 1px solid ${colors.neutral[200]}; border-radius: 12px;
  padding: 18px 18px 8px; box-shadow: ${colors.shadows?.sm || '0 1px 3px rgba(0,0,0,0.06)'};
  ${p => p.$wide && `grid-column: 1 / -1;`}
`;
const CardTitle = styled.div`
  font-size: 14px; font-weight: 600; color: ${colors.text.primary}; margin-bottom: 12px;
`;
const Empty = styled.div`
  padding: 40px 0; text-align: center; color: ${colors.text.secondary}; font-size: 13px;
`;

export default InsightsTab;
